import { create } from 'zustand'

export type FileStatus = 'pending' | 'processing' | 'success' | 'error'
export type FileKind = 'image' | 'video' | 'audio' | 'unknown'

export interface ToolFileEntry {
    id: string
    file: File
    status: FileStatus
    progressPercent: number
    progressText: string
    resultUrl: string | null
    resultSize: number
    errorMsg: string
}

export interface ToolStore {
    files: ToolFileEntry[]
    quality: number
    targetFormat: string
    fileKind: FileKind

    addFiles: (newFiles: File[], validate?: (f: File) => boolean) => void
    removeFile: (id: string) => void
    updateFile: (id: string, updates: Partial<ToolFileEntry>) => void
    setQuality: (q: number) => void
    setTargetFormat: (f: string) => void
    setFileKind: (k: FileKind) => void
    reset: () => void
}

interface StoreDefaults {
    quality?: number
    targetFormat?: string
    fileKind?: FileKind
}

export function createToolStore(defaults: StoreDefaults = {}) {
    const defaultQuality = defaults.quality ?? 60
    const defaultFormat = defaults.targetFormat ?? ''
    const defaultKind = defaults.fileKind ?? 'unknown'

    return create<ToolStore>()((set, get) => ({
        files: [],
        quality: defaultQuality,
        targetFormat: defaultFormat,
        fileKind: defaultKind,

        addFiles: (newFiles, validate) => {
            const filtered = validate ? newFiles.filter(validate) : newFiles
            if (filtered.length === 0) return

            const entries: ToolFileEntry[] = filtered.map(f => ({
                id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
                file: f,
                status: 'pending' as const,
                progressPercent: 0,
                progressText: '',
                resultUrl: null,
                resultSize: 0,
                errorMsg: '',
            }))

            set(state => ({ files: [...state.files, ...entries] }))
        },

        removeFile: (id) => {
            const entry = get().files.find(f => f.id === id)
            if (entry?.resultUrl) URL.revokeObjectURL(entry.resultUrl)
            set(state => ({ files: state.files.filter(f => f.id !== id) }))
        },

        updateFile: (id, updates) => {
            set(state => ({
                files: state.files.map(f => f.id === id ? { ...f, ...updates } : f),
            }))
        },

        setQuality: (q) => set({ quality: q }),
        setTargetFormat: (f) => set({ targetFormat: f }),
        setFileKind: (k) => set({ fileKind: k }),

        reset: () => {
            get().files.forEach(f => {
                if (f.resultUrl) URL.revokeObjectURL(f.resultUrl)
            })
            set({
                files: [],
                quality: defaultQuality,
                targetFormat: defaultFormat,
                fileKind: defaultKind,
            })
        },
    }))
}
